import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavItem {
  icon: string;
  activeIcon: string;
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { icon: 'ri-home-4-line', activeIcon: 'ri-home-4-fill', label: 'Home', href: '/' },
  { icon: 'ri-apps-2-line', activeIcon: 'ri-apps-2-fill', label: 'Solutions', href: '#solutions' },
  { icon: 'ri-lightbulb-line', activeIcon: 'ri-lightbulb-fill', label: 'Why Us', href: '/why-us' },
  { icon: 'ri-mail-send-line', activeIcon: 'ri-mail-send-fill', label: 'Contact', href: '/contact' },
];

const solutionsData = [
  {
    icon: 'ri-barcode-line',
    title: 'Processing',
    description: 'Clean, grade, weigh, vacuum-pack, and standardise.',
    href: '/processing',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Storage',
    description: 'List your warehouse capacity, get matched demand.',
    href: '/storage',
  },
  {
    icon: 'ri-truck-line',
    title: 'Transport',
    description: "Lease your reefer van's spare space, move produce on demand.",
    href: '/transport',
  },
  {
    icon: 'ri-exchange-line',
    title: 'Wholesale',
    description: 'Trade frozen seafood, meat & vegetables B2B, at scale.',
    href: '/wholesale',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Retail',
    description: 'Hygienic, standard-cut frozen food, delivered home.',
    href: '/retail',
  },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const [solutionsOpen, setSolutionsOpen] = useState(false);

  // Bottom nav always visible on mobile

  // Close solutions sheet on route change
  useEffect(() => {
    setSolutionsOpen(false);
  }, [location.pathname]);

  const isActive = (item: NavItem) => {
    if (item.href === '#solutions') return solutionsOpen;
    if (item.href === '/') return location.pathname === '/';
    return location.pathname.startsWith(item.href);
  };

  const handleSolutionsClick = () => {
    setSolutionsOpen(!solutionsOpen);
  };

  return (
    <>
      {/* Bottom Nav Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden transition-transform duration-300 translate-y-0">
        <div className="bg-brand-500 safe-area-bottom">
          <div className="flex items-center justify-around h-16 px-2">
            {navItems.map((item) => {
              const active = isActive(item);

              if (item.href === '#solutions') {
                return (
                  <button
                    key={item.label}
                    onClick={handleSolutionsClick}
                    className={`relative flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors cursor-pointer ${
                      active ? 'text-secondary-400' : 'text-paper-50/50'
                    }`}
                  >
                    {active && (
                      <span className="absolute -top-1 w-1 h-1 rounded-full bg-secondary-400"></span>
                    )}
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${active ? item.activeIcon : item.icon} text-lg`}></i>
                    </div>
                    <span className="text-[10px] font-medium whitespace-nowrap">{item.label}</span>
                  </button>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`relative flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1 transition-colors ${
                    active ? 'text-secondary-400' : 'text-paper-50/50'
                  }`}
                >
                  {active && (
                    <span className="absolute -top-1 w-1 h-1 rounded-full bg-secondary-400"></span>
                  )}
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${active ? item.activeIcon : item.icon} text-lg`}></i>
                  </div>
                  <span className="text-[10px] font-medium whitespace-nowrap">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Solutions Bottom Sheet Overlay */}
      {solutionsOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm"
            onClick={() => setSolutionsOpen(false)}
          ></div>

          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-ink-900 rounded-t-2xl animate-slide-up max-h-[70vh] overflow-y-auto">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-ink-500"></div>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3">
              <h3 className="font-heading text-base font-bold text-paper-50">
                Our Solutions
              </h3>
              <button
                onClick={() => setSolutionsOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-ink-700 text-paper-300 hover:bg-ink-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Pillar Cards */}
            <div className="px-5 pb-8 space-y-3">
              {solutionsData.map((sol) => (
                <Link
                  key={sol.title}
                  to={sol.href}
                  onClick={() => setSolutionsOpen(false)}
                  className="flex items-center gap-4 p-4 rounded-xl bg-ink-800/60 border border-ink-600/40 active:scale-[0.98] transition-transform cursor-pointer"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-secondary-900/40 text-secondary-400 shrink-0">
                    <i className={`${sol.icon} text-lg`}></i>
                  </div>
                  <div className="min-w-0">
                    <span className="font-heading font-semibold text-sm text-secondary-400">
                      {sol.title}
                    </span>
                    <p className="text-xs text-paper-400 mt-0.5 leading-relaxed">
                      {sol.description}
                    </p>
                  </div>
                  <div className="ml-auto shrink-0">
                    <i className="ri-arrow-right-s-line text-paper-500"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}